import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatEmitInfo } from '../chat/chat.component';
import { ChatService } from '../../service/chat-service';

export interface ErrandDialogReference {
  id: string;
  dialogId?: string;
}

@Component({
  selector: 'app-errand-dialog',
  templateUrl: './errand-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ErrandDialogComponent implements OnChanges, OnDestroy {
  @Input() errand: ErrandDialogReference;
  @Input() dialogEndpoint = 'errand/dialog';
  @Input() visibleForTenantCheckbox = false;
  @Input() isTenantInvolved = false;
  @Output() dialogChange = new EventEmitter<ChatEmitInfo>();

  repairFailed = false;
  repairing = false;

  private requestVersion = 0;
  private attemptedErrandId: string;
  private requestSubscription?: Subscription;

  constructor(
    private readonly chatService: ChatService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.errand) this.ensureDialog(false);
  }

  retry(): void {
    this.ensureDialog(true);
  }

  ngOnDestroy(): void {
    this.requestVersion++;
    this.requestSubscription?.unsubscribe();
  }

  private ensureDialog(force: boolean): void {
    const errand = this.errand;
    if (this.attemptedErrandId && this.attemptedErrandId !== errand?.id) {
      this.attemptedErrandId = undefined;
    }
    if (!this.isPersistedId(errand?.id) || errand.dialogId) {
      this.requestVersion++;
      this.requestSubscription?.unsubscribe();
      this.requestSubscription = undefined;
      this.repairFailed = false;
      this.repairing = false;
      return;
    }
    if (!force && this.attemptedErrandId === errand.id) return;

    const errandId = errand.id;
    const version = ++this.requestVersion;
    this.attemptedErrandId = errandId;
    this.repairFailed = false;
    this.repairing = true;
    this.requestSubscription?.unsubscribe();
    this.requestSubscription = this.chatService.ensureErrandDialog(errandId).subscribe({
      next: result => {
        if (!this.isCurrent(version, errandId)) return;
        this.errand.dialogId = result.dialogId;
        this.repairing = false;
        this.changeDetectorRef.markForCheck();
      },
      error: () => {
        if (!this.isCurrent(version, errandId)) return;
        this.repairing = false;
        this.repairFailed = true;
        this.changeDetectorRef.markForCheck();
      },
    });
  }

  private isCurrent(version: number, errandId: string): boolean {
    return version === this.requestVersion && this.errand?.id === errandId;
  }

  private isPersistedId(id: string): boolean {
    return !!id
      && id !== '00000000-0000-0000-0000-000000000000'
      && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
  }
}
